import React, { useMemo } from 'react';
import { OI_SLIDER_MAX } from '../../../utils/liquidityRule';
import CollapsibleFilterSection from './CollapsibleFilterSection';
import CheckboxFilterGroup from './CheckboxFilterGroup';
import RangeSliderWithHistogram from './RangeSliderWithHistogram';
import InfoTooltip from '../../common/InfoTooltip';
import { AdvancedFilters, DistributionResponse, DistributionBucket, COVERED_CALL_DEFAULT_FILTERS } from './types';
import { SECTOR_CATEGORIES, MARKET_CAP_CATEGORIES, TREND_SIGNALS } from './SectorMapping';

/**
 * Screener filters, grouped the way the stock card reads (Oct 2026, "B+"):
 * Score → Income → Setup → Risk flags → Company (not scored) → Market.
 * Shared by the desktop sidebar and the mobile drawer so both stay identical.
 * Copy is factual (no "recommended"/"best for CC"): the user sets the rules.
 */

const TIPS = {
  incomeFit: `Income fit (0–50): how selling options on this stock has paid historically (25) plus premium yield (25). Company fundamentals are not scored.`,
  timing: `Timing (0–50): the track record of today's setup (35) plus earnings and calendar risk (15).`,
  premiumYield: `Premium of the best contract, annualized, priced at the last trade.`,
  yieldOnCash: `Premium of the best put, annualized, on the cash set aside. Priced at the last trade.`,
  baseline: (contract: string) => `Average result of a 35-day ${contract} on the stock in the backtest, across all setups.`,
  dividend: `Annual dividend as a % of price. Not part of the score. Ex-dividend dates can bring early assignment on calls.`,
  setupVsUsual: `How 35-day results in today's setup compare with the stock's usual result, for the stock itself and for similar stocks (peers). "Above" and "below" mean more than 1.5 points away. The stock's own figure needs at least 30 trading days in this setup.`,
  trend: `Price change over the last 12 weeks (60 trading days).`,
  earnings: `Earnings can move the price sharply. Use this to keep earnings out of (or inside) your window.`,
  spread: `Bid/ask spread of the stock's options, as a % of the mid price. Wider spreads cost more to trade. Default: 10% or less (the liquidity rule).`,
  openInterest: `Highest open interest across the stock's tradable contracts. Default: 50 or more (the liquidity rule).`,
  netMargin: `Profit per dollar of revenue. Display only: not part of the score.`,
  debtEquity: `Debt relative to equity. Financials and REITs run naturally higher. Display only: not part of the score.`,
  marketCap: `Company size by market value.`,
  sector: `Industry group.`,
  ivRank: `Today's implied volatility within its 52-week range (0 = lowest, 100 = highest).`,
  rsi: `14-day momentum, 0–100. Above 70 often follows a sharp rise; below 30 a sharp fall.`,
  maSignal: `Moving-average state: price vs its averages, and 50/200-day crosses.`,
  macd: `MACD momentum: histogram rising, or MACD crossing above its signal line.`,
};

const SETUP_VS_USUAL = [
  { value: 'good_setup', label: 'Stock and peers above usual' },
  { value: 'acceptable', label: 'Peers above usual' },
  { value: 'tactical', label: 'Stock above usual, peers flat' },
  { value: 'unfavorable', label: 'Stock or peers below usual' },
];

const MA_LABELS: Record<string, string> = {
  'Golden Cross': 'Golden cross (50-day above 200-day)',
  Bullish: 'Price above 200-day',
  'Value Dip': 'Price below, 50-day above 200-day',
  Bearish: 'Price and 50-day below 200-day',
  'No Data': 'No data',
};

const MACD_SIGNALS = [
  { value: 'Rising', label: 'Histogram rising' },
  { value: 'Bull Cross', label: 'Crossed above signal' },
];

const EARNINGS_OPTIONS: { key: string; label: string; exclude?: number; within?: number }[] = [
  { key: 'any', label: 'Any' },
  { key: 'x7', label: 'None in the next 7 days', exclude: 7 },
  { key: 'x14', label: 'None in the next 14 days', exclude: 14 },
  { key: 'x30', label: 'None in the next 30 days', exclude: 30 },
  { key: 'w7', label: 'Earnings in the next 7 days', within: 7 },
];

const emptyDistribution: DistributionBucket[] = Array(10).fill({ min: 0, max: 10, count: 0, percentage: 0 });

const formatOI = (value: number): string =>
  value >= 1000 ? `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}K` : value.toString();

const signed = (v: number, digits = 1) => `${v > 0 ? '+' : ''}${v.toFixed(digits)}`;

/** Minimum-score picker on the dimension's 0–50 scale. Backend takes 1–5 (each = 10 points). */
const ScoreThreshold: React.FC<{
  label: string;
  value?: number;
  onChange: (level: number | undefined) => void;
  tooltip: string;
}> = ({ label, value, onChange, tooltip }) => (
  <div className="fs-field">
    <div className="fs-field-head">
      <span className="fs-label">{label}<InfoTooltip text={tooltip} /></span>
      <span className="fs-readout">{value !== undefined ? `≥ ${value * 10}` : 'Any'}<span className="fs-of"> /50</span></span>
    </div>
    <div className="fs-seg" role="radiogroup" aria-label={`${label}, minimum out of 50`}>
      {[undefined, 1, 2, 3, 4].map((level) => (
        <button
          key={level ?? 'any'}
          type="button"
          role="radio"
          aria-checked={value === level}
          className={value === level ? 'is-on' : ''}
          onClick={() => onChange(level)}
        >
          {level === undefined ? 'Any' : `${level * 10}+`}
        </button>
      ))}
    </div>
  </div>
);

const FieldLabel: React.FC<{ label: string; tooltip?: string }> = ({ label, tooltip }) => (
  <div className="fs-field-head">
    <span className="fs-label">{label}{tooltip && <InfoTooltip text={tooltip} />}</span>
  </div>
);

interface FilterSectionsProps {
  filters: AdvancedFilters;
  update: (updates: Partial<AdvancedFilters>) => void;
  distributionData: DistributionResponse | null;
  isCsp: boolean;
}

const arraysEqual = (a?: string[], b?: string[]) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.length === b.length && a.every((v) => b.includes(v));
};

const FilterSections: React.FC<FilterSectionsProps> = ({ filters: f, update, distributionData, isCsp }) => {
  const contract = isCsp ? 'put' : 'call';
  const dist = distributionData?.distributions;

  const counts = useMemo(() => {
    const on = (b: boolean) => (b ? 1 : 0);
    return {
      score: on(f.min_dimension1_stars !== undefined) + on(f.min_dimension2_stars !== undefined),
      income:
        on((f.annualized_yield_min ?? 0) > 0 || (f.annualized_yield_max ?? 100) < 100) +
        on((f.baseline_pnl_min ?? -3) > -3 || (f.baseline_pnl_max ?? 4) < 4) +
        on((f.dividend_yield_min ?? 0) > 0 || (f.dividend_yield_max ?? 15) < 15),
      setup: on(!!f.setup_signals?.length) + on((f.slope_12w_min ?? -50) > -50 || (f.slope_12w_max ?? 50) < 50),
      risk:
        on(f.exclude_earnings_within_days !== COVERED_CALL_DEFAULT_FILTERS.exclude_earnings_within_days || !!f.earnings_within_days) +
        on((f.spread_pct_min ?? 0) > 0 || (f.spread_pct_max ?? 30) !== (COVERED_CALL_DEFAULT_FILTERS.spread_pct_max ?? 30)) +
        on((f.open_interest_min ?? 0) !== (COVERED_CALL_DEFAULT_FILTERS.open_interest_min ?? 0) || (f.open_interest_max ?? OI_SLIDER_MAX) < OI_SLIDER_MAX),
      company:
        on(!arraysEqual(f.market_cap_categories, COVERED_CALL_DEFAULT_FILTERS.market_cap_categories)) +
        on(!!f.sectors?.length) +
        on((f.net_margin_min ?? -50) > -50 || (f.net_margin_max ?? 50) < 50) +
        on((f.debt_equity_max ?? 5) < 5),
      market:
        on((f.iv_rank_min ?? 0) > 0 || (f.iv_rank_max ?? 100) < 100) +
        on((f.rsi_min ?? 0) > 0 || (f.rsi_max ?? 100) < 100) +
        on(!!f.trend_signals?.length) +
        on(!!f.macd_signals?.length),
    };
  }, [f]);

  const sectorOptions = useMemo(
    () => SECTOR_CATEGORIES.map((c) => ({ value: c.value, label: c.label, count: distributionData?.sector_counts?.[c.value] ?? 0 })),
    [distributionData]
  );
  const marketCapOptions = useMemo(
    () => MARKET_CAP_CATEGORIES.map((c) => ({ value: c.value, label: `${c.label} (${c.description})`, count: distributionData?.market_cap_counts?.[c.value] ?? 0 })),
    [distributionData]
  );
  const trendOptions = useMemo(
    () => TREND_SIGNALS.map((t) => ({ value: t.value, label: MA_LABELS[t.value] ?? t.label, count: distributionData?.trend_counts?.[t.value] ?? 0 })),
    [distributionData]
  );

  const earningsKey =
    f.earnings_within_days === 7
      ? 'w7'
      : f.exclude_earnings_within_days
      ? `x${f.exclude_earnings_within_days}`
      : 'any';

  return (
    <>
      <CollapsibleFilterSection title={isCsp ? 'CSP Score' : 'CC Score'} activeFilterCount={counts.score}>
        <ScoreThreshold
          label="Income fit"
          value={f.min_dimension1_stars}
          onChange={(level) => update({ min_dimension1_stars: level })}
          tooltip={TIPS.incomeFit}
        />
        <ScoreThreshold
          label="Timing"
          value={f.min_dimension2_stars}
          onChange={(level) => update({ min_dimension2_stars: level })}
          tooltip={TIPS.timing}
        />
      </CollapsibleFilterSection>

      <CollapsibleFilterSection title="Income" activeFilterCount={counts.income}>
        <RangeSliderWithHistogram
          label={isCsp ? 'Premium / yr (on cash)' : 'Premium / yr'}
          min={0}
          max={100}
          step={5}
          value={[f.annualized_yield_min ?? 0, f.annualized_yield_max ?? 100]}
          onChange={([min, max]) => update({ annualized_yield_min: min, annualized_yield_max: max })}
          distribution={dist?.annualized_yield ?? emptyDistribution}
          unit="%"
          tooltip={isCsp ? TIPS.yieldOnCash : TIPS.premiumYield}
        />
        <RangeSliderWithHistogram
          label="Net result / 35 days"
          min={-3}
          max={4}
          step={0.25}
          value={[f.baseline_pnl_min ?? -3, f.baseline_pnl_max ?? 4]}
          onChange={([min, max]) => update({ baseline_pnl_min: min, baseline_pnl_max: max })}
          distribution={dist?.baseline_pnl ?? emptyDistribution}
          formatValue={(v) => signed(v)}
          unit="%"
          tooltip={TIPS.baseline(contract)}
        />
        <RangeSliderWithHistogram
          label="Dividend yield"
          min={0}
          max={15}
          step={0.5}
          value={[f.dividend_yield_min ?? 0, f.dividend_yield_max ?? 15]}
          onChange={([min, max]) => update({ dividend_yield_min: min, dividend_yield_max: max })}
          distribution={dist?.dividend_yield ?? emptyDistribution}
          unit="%"
          tooltip={TIPS.dividend}
        />
      </CollapsibleFilterSection>

      <CollapsibleFilterSection title="Setup" activeFilterCount={counts.setup}>
        <div className="fs-field">
          <FieldLabel label="This setup vs usual" tooltip={TIPS.setupVsUsual} />
          <CheckboxFilterGroup
            options={SETUP_VS_USUAL}
            selectedValues={f.setup_signals ?? []}
            onChange={(values) => update({ setup_signals: values })}
          />
        </div>
        <RangeSliderWithHistogram
          label="12-week trend"
          min={-50}
          max={50}
          step={5}
          value={[f.slope_12w_min ?? -50, f.slope_12w_max ?? 50]}
          onChange={([min, max]) => update({ slope_12w_min: min, slope_12w_max: max })}
          formatValue={(v) => signed(v, 0)}
          unit="%"
          tooltip={TIPS.trend}
        />
      </CollapsibleFilterSection>

      <CollapsibleFilterSection title="Risk flags" activeFilterCount={counts.risk}>
        <div className="fs-field">
          <FieldLabel label="Earnings" tooltip={TIPS.earnings} />
          <div className="fs-radios" role="radiogroup" aria-label="Earnings">
            {EARNINGS_OPTIONS.map((o) => (
              <label key={o.key} className={`fs-radio ${earningsKey === o.key ? 'is-on' : ''}`}>
                <input
                  type="radio"
                  name="fs-earnings"
                  checked={earningsKey === o.key}
                  onChange={() => update({ exclude_earnings_within_days: o.exclude, earnings_within_days: o.within })}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
        <RangeSliderWithHistogram
          label="Option spread"
          min={0}
          max={30}
          step={1}
          value={[f.spread_pct_min ?? 0, f.spread_pct_max ?? 30]}
          onChange={([min, max]) => update({ spread_pct_min: min, spread_pct_max: max })}
          distribution={dist?.spread_pct ?? emptyDistribution}
          unit="%"
          tooltip={TIPS.spread}
        />
        <RangeSliderWithHistogram
          label="Open interest"
          min={0}
          max={OI_SLIDER_MAX}
          step={25}
          value={[f.open_interest_min ?? 0, f.open_interest_max ?? OI_SLIDER_MAX]}
          onChange={([min, max]) => update({ open_interest_min: min, open_interest_max: max })}
          formatValue={formatOI}
          tooltip={TIPS.openInterest}
        />
      </CollapsibleFilterSection>

      <CollapsibleFilterSection title="Company" note="not scored" activeFilterCount={counts.company} defaultExpanded={false}>
        <RangeSliderWithHistogram
          label="Net margin"
          min={-50}
          max={50}
          step={5}
          value={[f.net_margin_min ?? -50, f.net_margin_max ?? 50]}
          onChange={([min, max]) => update({ net_margin_min: min, net_margin_max: max })}
          distribution={dist?.net_margin ?? emptyDistribution}
          unit="%"
          tooltip={TIPS.netMargin}
        />
        <RangeSliderWithHistogram
          label="Debt / equity"
          min={0}
          max={5}
          step={0.25}
          value={[0, f.debt_equity_max ?? 5]}
          onChange={([, max]) => update({ debt_equity_max: max })}
          distribution={dist?.debt_equity ?? emptyDistribution}
          formatValue={(v) => v.toFixed(2)}
          unit="×"
          tooltip={TIPS.debtEquity}
        />
        <div className="fs-field">
          <FieldLabel label="Market cap" tooltip={TIPS.marketCap} />
          <CheckboxFilterGroup
            options={marketCapOptions}
            selectedValues={f.market_cap_categories ?? []}
            onChange={(values) => update({ market_cap_categories: values })}
          />
        </div>
        <div className="fs-field">
          <FieldLabel label="Sector" tooltip={TIPS.sector} />
          <CheckboxFilterGroup
            options={sectorOptions}
            selectedValues={f.sectors ?? []}
            onChange={(values) => update({ sectors: values })}
          />
        </div>
      </CollapsibleFilterSection>

      <CollapsibleFilterSection title="Market" activeFilterCount={counts.market} defaultExpanded={false}>
        <RangeSliderWithHistogram
          label="IV rank"
          min={0}
          max={100}
          step={5}
          value={[f.iv_rank_min ?? 0, f.iv_rank_max ?? 100]}
          onChange={([min, max]) => update({ iv_rank_min: min, iv_rank_max: max })}
          distribution={dist?.iv_rank ?? emptyDistribution}
          tooltip={TIPS.ivRank}
        />
        <RangeSliderWithHistogram
          label="RSI (14-day)"
          min={0}
          max={100}
          step={5}
          value={[f.rsi_min ?? 0, f.rsi_max ?? 100]}
          onChange={([min, max]) => update({ rsi_min: min, rsi_max: max })}
          distribution={dist?.rsi ?? emptyDistribution}
          tooltip={TIPS.rsi}
        />
        <div className="fs-field">
          <FieldLabel label="Moving averages" tooltip={TIPS.maSignal} />
          <CheckboxFilterGroup
            options={trendOptions}
            selectedValues={f.trend_signals ?? []}
            onChange={(values) => update({ trend_signals: values })}
          />
        </div>
        <div className="fs-field">
          <FieldLabel label="MACD" tooltip={TIPS.macd} />
          <CheckboxFilterGroup
            options={MACD_SIGNALS}
            selectedValues={f.macd_signals ?? []}
            onChange={(values) => update({ macd_signals: values })}
          />
        </div>
      </CollapsibleFilterSection>
    </>
  );
};

export default FilterSections;
